import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft, Zap, Building, User, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { PRICE_IDS, redirectToCheckout } from '../lib/stripe';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Essential clinical support for healthcare workers',
    features: [
      '25 Clinical Conditions',
      '15 Essential Medicines',
      'Basic Search',
      'Emergency Protocols',
      'Mobile Access',
      'English Language'
    ],
    cta: 'Get Started',
    popular: false,
    priceId: null
  },
  {
    name: 'Professional',
    price: '$29',
    period: '/month',
    description: 'Full access for healthcare professionals',
    features: [
      '100 Clinical Conditions',
      '50 Essential Medicines',
      'Advanced Search & Filters',
      'All Emergency Protocols',
      'Clinical Calculators',
      '5 Languages (EN, FR, PT, AR, SW)',
      'Offline Access (PWA)',
      'Evidence Grades & References',
      'Drug Interaction Checker',
      'Bookmarks & Search History',
      'Priority Support'
    ],
    cta: 'Subscribe Now',
    popular: true,
    priceId: PRICE_IDS.professional
  },
  {
    name: 'Enterprise',
    price: '$99',
    period: '/month',
    description: 'For hospitals and healthcare organizations',
    features: [
      'Everything in Professional',
      'Unlimited Team Members',
      'API Access',
      'Custom Branding',
      'Admin Dashboard',
      'Usage Analytics',
      'SSO Integration',
      'HIPAA Compliance Tools',
      'Dedicated Account Manager',
      'Custom Protocol Development',
      'Training & Onboarding'
    ],
    cta: 'Subscribe Now',
    popular: false,
    priceId: PRICE_IDS.enterprise
  }
];

export default function PricingPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleSubscribe = async (planName: string, priceId: string | null) => {
    if (!priceId) {
      window.location.href = '/auth/register';
      return;
    }

    if (!user) {
      window.location.href = '/auth/login?redirect=pricing';
      return;
    }

    setLoadingPlan(planName);

    try {
      await redirectToCheckout(priceId);
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again.');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">{t('pricing')}</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Access evidence-based clinical decision support designed for African healthcare settings
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${
                plan.popular ? 'ring-2 ring-blue-500 scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-center py-2 text-sm font-semibold">
                  <Zap className="w-4 h-4 inline mr-1" />
                  {t('mostPopular')}
                </div>
              )}

              <div className={`p-8 ${plan.popular ? 'pt-14' : ''}`}>
                <div className="flex items-center gap-2 mb-4">
                  {plan.name === 'Free' && <User className="w-6 h-6 text-gray-400" />}
                  {plan.name === 'Professional' && <Zap className="w-6 h-6 text-blue-500" />}
                  {plan.name === 'Enterprise' && <Building className="w-6 h-6 text-purple-500" />}
                  <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                </div>

                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500">{plan.period}</span>
                </div>

                <p className="text-gray-600 mb-6">{plan.description}</p>

                <button
                  onClick={() => handleSubscribe(plan.name, plan.priceId)}
                  disabled={loadingPlan === plan.name}
                  className={`w-full py-3 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:bg-gray-100'
                  }`}
                >
                  {loadingPlan === plan.name ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    plan.cta
                  )}
                </button>

                <ul className="mt-8 space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 ${
                        plan.popular ? 'text-blue-500' : 'text-green-500'
                      }`} />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600">
            Need a custom solution? Contact us at{' '}
            <a href="mailto:ceo@omnialera.com" className="text-blue-600 hover:underline">
              ceo@omnialera.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
